/* eslint-disable @typescript-eslint/no-explicit-any -- Disable to allow generic TanStack Form types */
import { useMemo } from 'react';
import { z } from 'zod';
import { useFormPage, defineFormPage } from '@repo/form';
import { FormActions } from '@repo/web-form';
import { FormField, Input } from './FormField.js';
import { FormSelect } from './FormSelect.js';
import { Banner } from './Banner.js';

export interface FieldConfigItem {
  widget?: 'input' | 'select' | 'checkbox' | 'date' | 'number' | 'textarea';
  label: string;
  options?: { value: string; label: string }[];
  placeholder?: string;
  order?: number;
  render?: (field: any, isSubmitting: boolean, err?: string) => React.ReactNode;
}

export interface SchemaFormProps<TSchema extends z.ZodObject<any>> {
  schema: TSchema;
  defaultValues: z.input<TSchema>;
  fieldConfig: Partial<Record<keyof z.infer<TSchema>, FieldConfigItem>>;
  mutation: { mutateAsync: (values: any) => Promise<any> };
  onSuccess: () => void;
  onError?: (err: Error) => void;
  onBeforeSubmit?: (values: z.input<TSchema>) => any;
  routeId?: string;
  enableDraft?: boolean;
  submitLabel?: string;
  onCancel?: () => void;
}

export function SchemaForm<TSchema extends z.ZodObject<any>>({
  schema,
  defaultValues,
  fieldConfig,
  mutation,
  onSuccess,
  onError,
  onBeforeSubmit,
  routeId,
  enableDraft = false,
  submitLabel = 'Save',
  onCancel,
}: SchemaFormProps<TSchema>) {
  const config = useMemo(() => {
    return defineFormPage<z.input<TSchema>>({
      resource: routeId || 'resource',
      defaultValues,
      schema: schema as any,
    });
  }, [routeId, defaultValues, schema]);

  const {
    form,
    isSubmitting,
    handleSubmit,
    fieldError,
    hasDraft,
    restoreDraft,
    clearDraft,
    dismissDraft,
  } = useFormPage(config, mutation, {
    routeId,
    enableDraft,
    onBeforeSubmit,
    onSuccess,
    onError,
  });

  const sortedKeys = useMemo(() => {
    const keys = Object.keys(schema.shape);
    return keys.sort((a, b) => {
      const orderA = fieldConfig[a]?.order ?? 999;
      const orderB = fieldConfig[b]?.order ?? 999;
      return orderA - orderB;
    });
  }, [schema, fieldConfig]);

  const getInnerZodType = (field: any): any => {
    let current = field;
    while (current && '_def' in current) {
      const typeName = current._def.typeName;
      if (typeName === 'ZodOptional' || typeName === 'ZodNullable' || typeName === 'ZodEffects') {
        current = current._def.innerType;
      } else if (typeName === 'ZodUnion' || typeName === 'ZodOr') {
        current = current._def.options?.[0] || current._def.left;
      } else {
        break;
      }
    }
    return current;
  };

  const inferWidget = (field: any): 'input' | 'select' | 'checkbox' | 'date' | 'number' | 'textarea' => {
    const inner = getInnerZodType(field);
    const typeName = inner?._def?.typeName || '';
    if (typeName === 'ZodBoolean') return 'checkbox';
    if (typeName === 'ZodDate') return 'date';
    if (typeName === 'ZodNumber') return 'number';
    if (typeName === 'ZodEnum') return 'select';
    return 'input';
  };

  const getEnumValues = (field: any): string[] => {
    const inner = getInnerZodType(field);
    if (inner?._def?.typeName === 'ZodEnum') {
      return inner._def.values || [];
    }
    return [];
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl flex flex-col gap-6">
      {hasDraft && (
        <Banner
          variant="info"
          title="Restore Draft"
          description="You have an unsaved draft from a previous session. Would you like to restore it?"
          action={{
            label: 'Restore',
            onClick: () => {
              const draft = restoreDraft();
              if (draft) {
                Object.entries(draft).forEach(([key, val]) => {
                  form.setFieldValue(key as never, val as never);
                });
              }
              dismissDraft();
            },
          }}
          secondaryAction={{
            label: 'Discard',
            onClick: () => clearDraft(),
          }}
        />
      )}

      <div className="grid grid-cols-2 gap-6">
        {sortedKeys.map((key) => {
          const fieldSchema = schema.shape[key];
          const configItem = fieldConfig[key];
          const label = configItem?.label ?? String(key);
          
          // Determine if field is required by checking if it's optional or nullable
          let isRequired = true;
          let current = fieldSchema;
          while (current && '_def' in current) {
            const typeName = current._def.typeName;
            if (typeName === 'ZodOptional' || typeName === 'ZodNullable') {
              isRequired = false;
              break;
            }
            if (typeName === 'ZodEffects') {
              current = current._def.innerType;
            } else if (typeName === 'ZodUnion' || typeName === 'ZodOr') {
              const options = current._def.options || [current._def.left, current._def.right];
              if (options.some((o: any) => o._def?.typeName === 'ZodOptional' || o._def?.typeName === 'ZodNullable' || o._def?.typeName === 'ZodLiteral' && o._def?.value === '')) {
                isRequired = false;
                break;
              }
              current = options[0];
            } else {
              break;
            }
          }

          return (
            <form.Field
              key={String(key)}
              name={key as any}
              validators={{
                onChange: ({ value }: { value: any }) => {
                  const res = fieldSchema.safeParse(value);
                  return res.success ? undefined : res.error.issues[0]?.message;
                },
                onBlur: ({ value }: { value: any }) => {
                  const res = fieldSchema.safeParse(value);
                  return res.success ? undefined : res.error.issues[0]?.message;
                },
              }}
              children={(field: any) => {
                const err = fieldError(field.state.meta);

                if (configItem?.render) {
                  return configItem.render(field, isSubmitting, err);
                }

                const widget = configItem?.widget ?? inferWidget(fieldSchema);

                if (widget === 'select') {
                  const options = configItem?.options ?? getEnumValues(fieldSchema).map((v) => ({
                    value: v,
                    label: v.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()),
                  }));

                  return (
                    <FormSelect
                      label={label}
                      id={field.name}
                      value={field.state.value || ''}
                      onChange={(e) => field.handleChange(e.target.value)}
                      disabled={isSubmitting}
                      error={err}
                      options={options}
                      required={isRequired}
                    />
                  );
                }

                if (widget === 'checkbox') {
                  return (
                    <FormField label={label} htmlFor={field.name} error={err} required={isRequired}>
                      <div className="flex items-center h-9">
                        <input
                          type="checkbox"
                          id={field.name}
                          checked={!!field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.checked)}
                          disabled={isSubmitting}
                          className="w-4 h-4 rounded text-accent-500 focus:ring-accent-500 bg-surface-3 border-border"
                        />
                      </div>
                    </FormField>
                  );
                }

                if (widget === 'textarea') {
                  return (
                    <FormField label={label} htmlFor={field.name} error={err} required={isRequired}>
                      <textarea
                        id={field.name}
                        placeholder={configItem?.placeholder}
                        value={field.state.value ?? ''}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                        disabled={isSubmitting}
                        className="w-full min-h-[80px] p-3 bg-surface-3 border border-border rounded-[6px] text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent-500 focus:ring-1 focus:ring-accent-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                    </FormField>
                  );
                }

                const inputType = widget === 'number' ? 'number' : widget === 'date' ? 'date' : 'text';

                return (
                  <FormField label={label} htmlFor={field.name} error={err} required={isRequired}>
                    <Input
                      id={field.name}
                      type={inputType}
                      placeholder={configItem?.placeholder}
                      value={field.state.value ?? ''}
                      onBlur={field.handleBlur}
                      onChange={(e) => {
                        const val = e.target.value;
                        field.handleChange(widget === 'number' ? (val === '' ? undefined : Number(val)) : val);
                      }}
                      disabled={isSubmitting}
                      error={!!err}
                    />
                  </FormField>
                );
              }}
            />
          );
        })}
      </div>

      <FormActions onCancel={onCancel} isSubmitting={isSubmitting} submitLabel={submitLabel} />
    </form>
  );
}
